import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, KeyRound, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api, { mediaSrc } from "@/lib/api";

const PinGate = ({ room, onUnlock }) => {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/proof/${room.code}`, { params: { pin } });
      if (data.locked) {
        setError("That PIN doesn't match. Try again.");
      } else {
        onUnlock(data, pin);
      }
    } catch {
      setError("Could not verify right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="proof-pin-form" onSubmit={submit} className="relative w-full max-w-sm border border-ivory/10 bg-coal/80 backdrop-blur-xl p-8 text-center">
      <KeyRound size={20} className="text-amber mx-auto" />
      <p className="font-mono text-[9px] tracking-[0.35em] uppercase text-mute mt-5">Private proofing room</p>
      <h1 className="font-display text-2xl text-ivory tracking-wide mt-2">{room.client_name}</h1>
      {room.event && <p className="font-serif italic text-ash mt-1">{room.event}</p>}
      <input
        data-testid="proof-pin-input"
        className="field mt-8 text-center tracking-[0.6em] text-lg"
        inputMode="numeric"
        maxLength={6}
        placeholder="••••"
        value={pin}
        onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
        autoFocus
      />
      {error && <p data-testid="proof-pin-error" className="font-mono text-[11px] text-red-400 mt-3">{error}</p>}
      <button data-testid="proof-pin-submit" type="submit" disabled={busy || pin.length < 4} className="w-full mt-6 font-mono text-[11px] tracking-[0.3em] uppercase bg-ivory text-ink py-4 hover:bg-amber disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
        {busy ? <Loader2 size={14} className="animate-spin" /> : null} Enter Room
      </button>
    </form>
  );
};

export default function Proof() {
  const { code } = useParams();
  const [state, setState] = useState({ status: "loading" });
  const [pin, setPin] = useState("");
  const [favs, setFavs] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [onlyFavs, setOnlyFavs] = useState(false);

  useEffect(() => {
    api
      .get(`/proof/${code}`)
      .then((r) => {
        if (r.data.locked) setState({ status: "locked", room: { ...r.data, code } });
        else {
          setState({ status: "open", room: r.data });
          setFavs(new Set(r.data.favorites));
        }
      })
      .catch(() => setState({ status: "missing" }));
  }, [code]);

  const unlock = (room, enteredPin) => {
    setPin(enteredPin);
    setFavs(new Set(room.favorites));
    setState({ status: "open", room });
  };

  const toggleFav = async (photo) => {
    const next = new Set(favs);
    next.has(photo.id) ? next.delete(photo.id) : next.add(photo.id);
    setFavs(next);
    try {
      const { data } = await api.post(`/proof/${code}/favorite`, { media_id: photo.id, pin });
      setFavs(new Set(data.favorites));
    } catch {
      setFavs(favs);
      toast.error("Couldn't save that favourite — try again.");
    }
  };

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <Loader2 className="animate-spin text-amber" size={22} />
      </div>
    );
  }

  if (state.status === "missing") {
    return (
      <div data-testid="proof-missing" className="min-h-screen bg-ink text-ivory flex flex-col items-center justify-center px-5 text-center">
        <div className="grain-overlay" aria-hidden />
        <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-mute">404 — No such room</p>
        <h1 className="font-display text-3xl sm:text-4xl mt-4 tracking-wide">This proofing room has closed.</h1>
        <Link to="/" className="font-mono text-[10px] tracking-[0.25em] uppercase text-amber mt-10 hover:text-ivory transition-colors">← Ansh Agarwal Films</Link>
      </div>
    );
  }

  if (state.status === "locked") {
    return (
      <div data-testid="proof-locked" className="min-h-screen bg-ink flex items-center justify-center px-5 relative">
        <div className="grain-overlay" aria-hidden />
        <PinGate room={state.room} onUnlock={unlock} />
      </div>
    );
  }

  const { room } = state;
  const photos = onlyFavs ? room.photos.filter((p) => favs.has(p.id)) : room.photos;

  return (
    <div data-testid="proof-room" className="min-h-screen bg-ink text-ivory relative">
      <div className="grain-overlay" aria-hidden />
      <header className="sticky top-0 z-40 border-b border-ivory/10 bg-ink/80 backdrop-blur-xl">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="ANSH AGARWAL FILMS" className="w-9 h-9 object-cover rounded-sm ring-1 ring-ivory/20" />
            <span className="font-display text-[11px] tracking-[0.3em] uppercase hidden sm:inline">Ansh Agarwal Films</span>
          </Link>
          <div className="flex items-center gap-3">
            <span data-testid="proof-fav-count" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ash flex items-center gap-2">
              <Heart size={12} className="text-amber fill-amber" /> {favs.size} selected
            </span>
            <button
              data-testid="proof-filter-favs"
              aria-pressed={onlyFavs}
              onClick={() => setOnlyFavs((v) => !v)}
              className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-all ${onlyFavs ? "bg-ivory text-ink border-ivory" : "border-line text-ash hover:text-ivory"}`}
            >
              {onlyFavs ? "Show All" : "Favourites"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 sm:px-10 py-14 sm:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono text-[9px] tracking-[0.35em] uppercase text-amber">Private proofing room</p>
          <h1 data-testid="proof-client-name" className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-wide mt-4">{room.client_name}</h1>
          {room.event && <p className="font-serif italic text-lg text-cream/85 mt-3">{room.event}</p>}
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-mute mt-6 max-w-xl leading-relaxed">
            {room.note || "Tap the heart on every frame you love. Your picks are saved instantly and shared with Ansh."}
          </p>
          <p data-testid="proof-protection-note" className="font-mono text-[9px] tracking-[0.2em] uppercase text-mute/70 mt-3 flex items-center gap-1.5">
            <ShieldCheck size={10} className="text-amber" /> Watermarked proofs · final files delivered separately
          </p>
        </motion.div>

        {photos.length === 0 ? (
          <p className="font-mono text-xs tracking-[0.2em] uppercase text-mute border border-dashed border-line p-16 text-center mt-14">
            {onlyFavs ? "No favourites yet — tap a heart to start your selection." : "Frames are being developed — check back soon."}
          </p>
        ) : (
          <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-14" data-testid="proof-photo-grid">
            <AnimatePresence mode="popLayout">
              {photos.map((p, i) => {
                const on = favs.has(p.id);
                return (
                  <motion.figure
                    layout
                    key={p.id}
                    data-testid={`proof-photo-${i}`}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: Math.min(i * 0.03, 0.4) }}
                    className={`group relative aspect-[4/5] overflow-hidden border transition-colors duration-300 ${on ? "border-amber/70" : "border-ivory/10"}`}
                  >
                    <button data-testid={`proof-photo-open-${i}`} onClick={() => setSelected(p)} className="absolute inset-0 text-left">
                      <img src={mediaSrc(p)} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
                      <span className="absolute bottom-3 left-3 font-mono text-[9px] tracking-[0.25em] uppercase text-cream/80">{String(i + 1).padStart(3, "0")}</span>
                    </button>
                    <button
                      data-testid={`proof-photo-fav-${i}`}
                      aria-pressed={on}
                      aria-label={on ? "Remove from favourites" : "Add to favourites"}
                      onClick={() => toggleFav(p)}
                      className={`absolute top-3 right-3 w-10 h-10 flex items-center justify-center border backdrop-blur transition-all duration-300 ${
                        on ? "bg-amber text-ink border-amber scale-100" : "bg-ink/60 text-ivory border-ivory/20 hover:border-amber hover:text-amber"
                      }`}
                    >
                      <Heart size={15} className={on ? "fill-ink" : ""} />
                    </button>
                  </motion.figure>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </main>

      <AnimatePresence>
        {selected && (
          <motion.div data-testid="proof-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelected(null)} className="fixed inset-0 z-[80] bg-ink/95 backdrop-blur-md flex items-center justify-center p-5 sm:p-14">
            <button data-testid="proof-lightbox-close" aria-label="Close" onClick={() => setSelected(null)} className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center border border-ivory/20 text-ivory hover:border-amber hover:text-amber transition-colors z-10">
              <X size={16} />
            </button>
            <motion.figure initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full">
              <img src={mediaSrc(selected)} alt={selected.title} className="w-full max-h-[74vh] object-contain border border-ivory/10" />
              <figcaption className="flex items-center justify-between gap-3 mt-4">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ash">{selected.title}</span>
                <button
                  data-testid="proof-lightbox-fav"
                  onClick={() => toggleFav(selected)}
                  className={`font-mono text-[10px] tracking-[0.25em] uppercase px-5 py-2.5 border flex items-center gap-2 transition-all ${favs.has(selected.id) ? "bg-amber text-ink border-amber" : "border-ivory/20 text-ivory hover:border-amber hover:text-amber"}`}
                >
                  <Heart size={12} className={favs.has(selected.id) ? "fill-ink" : ""} /> {favs.has(selected.id) ? "Favourited" : "Favourite"}
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
